import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageShell, btnGhost, btnPrimary, inputCls } from "@/components/TopBar";
import { BrandMark, DEFAULT_BRAND } from "@/context/BrandContext";

const MAX_BYTES = 300 * 1024;

export default function Branding() {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings");
    setBrand({
      brand_name: data.brand_name || DEFAULT_BRAND.brand_name,
      brand_tagline: data.brand_tagline || DEFAULT_BRAND.brand_tagline,
      brand_logo_url: data.brand_logo_url || null,
      brand_website: data.brand_website || "",
      brand_support_email: data.brand_support_email || "",
      brand_accent: data.brand_accent || DEFAULT_BRAND.brand_accent,
    });
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load branding"));
  }, [load]);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Logo must be under 300 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, brand_logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!brand.brand_name.trim()) return toast.error("Company name is required");
    setBusy(true);
    try {
      await api.patch("/settings", {
        brand_name: brand.brand_name.trim(),
        brand_tagline: brand.brand_tagline.trim(),
        brand_logo_url: brand.brand_logo_url,
        brand_website: brand.brand_website?.trim() || null,
        brand_support_email: brand.brand_support_email?.trim() || null,
        brand_accent: brand.brand_accent,
      });
      toast.success("Branding saved - reload to see it everywhere");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell
      testid="branding-page"
      subtitle="administration"
      title="BRANDING"
      actions={
        <button data-testid="save-branding-btn" onClick={save} disabled={busy} className={btnPrimary}>
          {busy ? "saving" : "save branding"}
        </button>
      }
    >
      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="grid gap-5 border border-white/15 bg-[#121212] p-6 sm:grid-cols-2">
          <div>
            <label className="overline mb-2 block">company name</label>
            <input
              data-testid="brand-name-input"
              value={brand.brand_name}
              onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">tagline</label>
            <input
              data-testid="brand-tagline-input"
              value={brand.brand_tagline}
              onChange={(e) => setBrand({ ...brand, brand_tagline: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">website (optional)</label>
            <input
              data-testid="brand-website-input"
              value={brand.brand_website}
              onChange={(e) => setBrand({ ...brand, brand_website: e.target.value })}
              placeholder="www.yourcompany.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">support email (optional)</label>
            <input
              data-testid="brand-email-input"
              value={brand.brand_support_email}
              onChange={(e) => setBrand({ ...brand, brand_support_email: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">accent colour</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                data-testid="brand-accent-input"
                value={brand.brand_accent}
                onChange={(e) => setBrand({ ...brand, brand_accent: e.target.value })}
                className="h-9 w-14 border border-white/15 bg-[#121212]"
              />
              <span className="text-xs text-[#A1A1AA]">{brand.brand_accent}</span>
            </div>
          </div>
          <div>
            <label className="overline mb-2 block">logo (png/svg, under 300 KB)</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={pickLogo}
                data-testid="brand-logo-input"
                className="hidden"
              />
              <button onClick={() => fileRef.current?.click()} className={btnGhost} data-testid="brand-logo-pick-btn">
                <Upload size={12} /> UPLOAD LOGO
              </button>
              {brand.brand_logo_url && (
                <button
                  onClick={() => setBrand({ ...brand, brand_logo_url: null })}
                  data-testid="brand-logo-clear-btn"
                  className="flex items-center gap-1.5 border border-white/10 px-2 py-2 text-[10px] tracking-[0.2em] text-[#A1A1AA] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                >
                  <X size={11} /> REMOVE
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border border-white/15 bg-[#0A0A0A] p-6" data-testid="branding-preview">
          <p className="overline mb-4">client page preview</p>
          <div className="border border-white/10 bg-[#121212] p-5">
            <BrandMark brand={brand} size={22} showTagline />
            <p className="mt-5 font-head text-2xl font-black tracking-tight">NL01AG4866</p>
            <p className="mt-1 text-[11px] text-[#A1A1AA]">Dimapur → Guwahati</p>
            <div className="mt-4 h-[2px] w-full bg-white/10">
              <div className="h-full" style={{ width: "62%", background: brand.brand_accent }} />
            </div>
            <p className="overline mt-4">
              {brand.brand_website || "your website"} · {brand.brand_support_email || "support email"}
            </p>
          </div>
          <p className="overline mt-5">
            this is what a customer sees on a shared tracking link - your name, your logo, your colour
          </p>
        </div>
      </div>
    </PageShell>
  );
}
